import * as yup from 'yup';
import {
  Criterion,
  ImpactToolInput,
  ImpactToolLiveResult,
  ImpactToolResult,
} from '~/types/impactTool';

/** Default colors */
export const blueColor = '#057c9c';
export const greenColor = '#63a615';
export const darkGreenColor = '#375f15';
export const redColor = '#c50014';
export const greyColor = '#212121';

/** Unit chars used across tool */
export enum ResultValueUnit {
  TONS = 't',
  EURO = '€',
  HOURS = 'Std.',
  KM = 'Kilometer',
  PERSON = 'Personen',
  NONE = '',
}

export const emptyCriterion: Criterion = {
  criterion: '',
  fullfilled: false,
  description: '',
};

/** Default criterions, which criterion name cannot edited by users */
export const defaultCriterions: Array<Criterion> = [
  {
    criterion:
      'Das Fahrradparkhaus setzt in unserer Kommune ein Zeichen für die Mobilitätswende, weg vom Auto, hin zum Fahrrad.',
    fullfilled: false,
    description: '',
    tooltip:
      'Das Fahrradparkhaus leistet Beiträge zu kommunalpolitischen Zielen oder übergeordneten Planungen (Kommunalentwicklungspläne, Verkehrsplanungen etc.).',
    default: true,
  },
  {
    criterion:
      'Das Fahrradparkhaus setzt Maßstäbe für Bauten der neuen Mobilität.',
    fullfilled: false,
    description: '',
    tooltip:
      'Das Fahrradparkhaus ist ein neuer Gebäudetypus, dessen architektonische Gestaltung die gleiche Aufmerksamkeit erfordert, wie andere bedeutende infrastrukturelle Bauwerke auch.',
    default: true,
  },
  {
    criterion:
      'Das Fahrradparkhaus bringt Besucher:innen von außen und einen Imagegewinn für unsere Kommune.',
    fullfilled: false,
    description: '',
    tooltip:
      'Das Fahrradparkhaus spielt eine Vorreiterrolle und wird zur touristischen und fachlichen Attraktion.',
    default: true,
  },
  {
    criterion:
      'Das Fahrradparkhaus wertet über seine architektonische Qualität das Stadtbild in unserem Bahnhofsumfeld auf und macht es auch für Nicht-Bahnreisende zum Aufenthaltsort.',
    fullfilled: false,
    description: '',
    tooltip:
      'Das Fahrradparkhaus ist keineswegs lediglich auf seine Grundfunktion, Abstellplätze zu bieten, beschränkt. Es muss vor allem für die Nutzer:innen attraktiv gestaltet werden. Darüber hinaus gestaltet es über seine Fassade das Bahnhofsumfeld - das Entree zur Stadt - mit.',
    default: true,
  },
  {
    criterion:
      'Das Fahrradparkhaus befreit unseren Bahnhofsvorplatz von wild abgestellten Fahrrädern, schafft mehr Platz für Fußgänger:innen und steigert die Aufenthaltsqualität.',
    fullfilled: false,
    description: '',
    tooltip:
      'Die Ordnung des Fahrradparkens im Bahnhofsumfeld kann einen Beitrag dazu leisten, die Konkurrenz zwischen Fahrradfahrer:innen und Fußgänger:innen zu verringern. Zudem können freiwerdende Flächen für eine Verbesserung der Aufenthaltsqualität genutzt werden.',
    default: true,
  },
  {
    criterion:
      'Die aufgrund des Fahrradparkhauses zunehmende Anzahl an Radfahrer:innen belebt unsere Innenstadt und stärkt den dortigen Einzelhandel.',
    fullfilled: false,
    description: '',
    tooltip:
      'Radfahrer:innen und Fußgänger:innen beleben die Innenstädte und den dort ansässigen Einzelhandel. Studien zeigen, dass bei Einkäufen mit dem Fahrrad mehr Umsatz erzeugt wird (mehr kleinere anstatt weniger Großeinkäufe).',
    default: true,
  },
  {
    criterion:
      'Das Fahrradparkhaus ergänzt unseren Bahnhof um weitere Dienstleistungen rund ums Fahrrad.',
    fullfilled: false,
    description: '',
    tooltip:
      'Z.B. Fahrradladen, Fahrradwerkstatt, Leihstation, Waschanlage, Umkleiden, Duschen, WC.',
    default: true,
  },
  {
    criterion:
      'Das Fahrradparkhaus trägt dazu bei Fahrraddiebstähle zu verringern.',
    fullfilled: false,
    description: '',
    tooltip:
      'Höherwertige Fahrräder werden genutzt, der polizeiliche und behördliche Aufwand wird verringert, das Sicherheitsgefühl am Bahnhof wächst.',
    default: true,
  },
  {
    criterion:
      'Eine verstärkte Nutzung höherwertiger Fahrräder, z. B. Pedelecs, vergrößert den Einzugsbereich unseres Bahnhofs.',
    fullfilled: false,
    description: '',
    tooltip:
      'Mit hochwertigen Fahrrädern werden größere Distanzen zurückgelegt.',
    default: true,
  },
  {
    criterion:
      'Das Fahrradparkhaus entlastet Züge durch die verringerte Mitnahme von Fahrrädern.',
    fullfilled: false,
    description: '',
    tooltip:
      'Konflikte aufgrund der Fahrradmitnahme in überlasteten Nahverkehrszügen werden verringert.',
    default: true,
  },
];

export const emptyInputData: ImpactToolInput = {
  location_name: '',
  project_description: '',
  number_new_parking: '',
  usage_new_parking: '',
  sum_construction_cost: '',
  own_calculation: undefined,
  percentage_pupils: '',
  percentage_pedelec: '',
  percentage_car: '',
  percentage_bike: '',
  percentage_trainandwalk: '',
  percentage_parkandride: '',
  distance_bikeandride: '',
  distance_car: '',
  percentage_municipal_car: '',
  distance_bike: '',
  duration_train: '',
  change_duration_distance: '',
};

/** Default values used in production */
export const initialValues: ImpactToolInput = {
  location_name: '',
  project_description: '',
  number_new_parking: '',
  usage_new_parking: '40',
  sum_construction_cost: '',
  own_calculation: undefined,
  percentage_pupils: '14',
  percentage_pedelec: '10',
  percentage_car: '21',
  percentage_bike: '4',
  percentage_trainandwalk: '72',
  percentage_parkandride: '3',
  distance_bikeandride: '3.5',
  distance_car: '32',
  percentage_municipal_car: '50',
  distance_bike: '10',
  duration_train: '30',
  change_duration_distance: '',
};

/** Default values for dev environment */
export const initialValuesDevelopment: ImpactToolInput = {
  location_name: ' Fahrradparkhaus am Bahnhof Wilmersdorf',
  project_description:
    'Anlage mit 200 zugangsgesicherten Plätzen am Südeingang des Bahnhofs. Entstehen soll ein moderner Fahrradturm und eine Sammelschließanlage, Bauzeit 2022-2023',
  number_new_parking: '128',
  usage_new_parking: '69',
  sum_construction_cost: '',
  own_calculation: undefined,
  percentage_pupils: '14',
  percentage_pedelec: '10',
  percentage_car: '21',
  percentage_bike: '4',
  percentage_trainandwalk: '72',
  percentage_parkandride: '3',
  distance_bikeandride: '3.5',
  distance_car: '32',
  percentage_municipal_car: '50',
  distance_bike: '10',
  duration_train: '30',
  change_duration_distance: '3',
};

/** Generate a new initial result  object */
export const resultSkeleton = (): ImpactToolResult => ({
  location_name: '',
  project_description: '',
  number_new_parking: 0,
  usage_new_parking: 0,
  sum_construction_cost: 0,
  own_calculation: undefined,
  reduced_air_pollution: 0,
  reduced_car_distance: 0,
  change_bike_distance_all: 0,
  change_bike_distance: 0,
  change_pedelec_distance: 0,
  reduced_co2_emissions: 0,
  reduced_co2_municipality: 0,
  reduced_co2_costs: 0,
  reduced_co2_costs_municipality: 0,
  reduced_vehicle_costs: 0,
  reduced_traveltime: 0,
  reduced_society_costs: 0,
  reduced_accident_costs: 0,
  active_persons: 0,
  distance_active_persons: 0,
  reduced_healthcare_costs: 0,
});

export const liveResultSkeleton = (): ImpactToolLiveResult => ({
  reduced_car_distance: 0,
  reduced_co2_emissions: 0,
});

/** Default messages for field errors */
yup.setLocale({
  mixed: {
    required: 'Diese Angabe ist verpflichtend',
  },
  number: {
    positive: 'Diese Zahl muss positiv sein',
    max: 'Maximal 100% möglich',
    integer: 'Diese Zahl muss ganzzahling sein',
  },
});

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const validationInputSchema = () =>
  yup.object({
    location_name: yup.string().required(),
    project_description: yup
      .string()
      .required()
      .max(400, 'Maximale Länge 400 Zeichen'),
    number_new_parking: yup.number().required().positive().integer(),
    usage_new_parking: yup.number().required().positive().integer().max(100),
    sum_construction_cost: yup.number().positive().integer(),
    own_calculation: yup.string().oneOf(['own', 'tool']),
    percentage_pupils: yup.number().required().positive().integer().max(100),
    percentage_pedelec: yup.number().required().positive().integer().max(100),
    percentage_car: yup.number().required().positive().integer().max(100),
    percentage_bike: yup.number().required().positive().integer().max(100),
    percentage_trainandwalk: yup
      .number()
      .required()
      .positive()
      .integer()
      .max(100),
    percentage_parkandride: yup
      .number()
      .required()
      .positive()
      .integer()
      .max(100),
    distance_bikeandride: yup.number().required().positive(),
    distance_car: yup.number().required().positive(),
    percentage_municipal_car: yup.number().positive().integer().max(100),
    distance_bike: yup.number().required().positive(),
    duration_train: yup.number().required().positive().integer(),
    change_duration_distance: yup.number().integer(),
  });

/** List of all required fields for input page */
export const requiredInputFields = [
  'location_name',
  'project_description',
  'number_new_parking',
  'usage_new_parking',
  'percentage_pupils',
  'percentage_pedelec',
  'percentage_car',
  'percentage_bike',
  'percentage_trainandwalk',
  'percentage_parkandride',
  'distance_bikeandride',
  'distance_car',
  'distance_bike',
  'duration_train',
];
